import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { LANGUAGES, LanguageCode } from '../i18n/config';
import { useAuth } from './AuthContext';
import { authApi } from '../services/api';

interface I18nContextType {
  language: LanguageCode;
  direction: 'ltr' | 'rtl';
  changeLanguage: (lang: LanguageCode) => Promise<void>;
  isRTL: boolean;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

interface I18nProviderProps {
  children: ReactNode;
}

export const I18nProvider = ({ children }: I18nProviderProps) => {
  const { i18n } = useTranslation();
  const { user, updateUser } = useAuth();

  // Get initial language from localStorage (i18next's detection key) or default to 'en'
  const getInitialLanguage = (): LanguageCode => {
    const storedLang = localStorage.getItem('i18nextLng');
    if (storedLang && LANGUAGES[storedLang as LanguageCode]) {
      return storedLang as LanguageCode;
    }
    return 'en';
  };

  const [language, setLanguage] = useState<LanguageCode>(() => {
    const initialLang = getInitialLanguage();
    // Ensure i18next is also set to this language on mount
    if (i18n.language !== initialLang) {
      i18n.changeLanguage(initialLang);
    }
    return initialLang;
  });
  const [direction, setDirection] = useState<'ltr' | 'rtl'>(LANGUAGES[language]?.dir || 'ltr');

  // Sync language between local storage and user profile on login
  useEffect(() => {
    const syncLanguageOnLogin = async () => {
      if (!user) return;

      const localLang = localStorage.getItem('i18nextLng') as LanguageCode | null;
      const serverLang = user.preferences?.language as LanguageCode | undefined;

      // If user has a locally selected language that differs from server preference,
      // keep the local language and update the server
      if (localLang && LANGUAGES[localLang] && localLang !== serverLang) {
        // Update server preference to match local choice
        try {
          await authApi.updateProfile({
            preferences: {
              ...user.preferences,
              language: localLang
            }
          });
          updateUser({
            ...user,
            preferences: {
              ...user.preferences,
              language: localLang
            }
          });
        } catch (error) {
          console.error('[I18nProvider] Failed to sync language to server:', error);
        }
      } else if (serverLang && LANGUAGES[serverLang] && serverLang !== language) {
        // If no local preference or it matches server, use server preference
        await i18n.changeLanguage(serverLang);
        setLanguage(serverLang);
        setDirection(LANGUAGES[serverLang].dir);
        localStorage.setItem('i18nextLng', serverLang);
      }
    };

    syncLanguageOnLogin();
  }, [user?.id]); // Only run when user changes (login/logout)

  // Update direction when language changes
  useEffect(() => {
    document.documentElement.dir = direction;
    document.documentElement.lang = language;

    // Add RTL class to body for RTL languages
    if (direction === 'rtl') {
      document.body.classList.add('rtl');
      document.body.classList.remove('ltr');
    } else {
      document.body.classList.add('ltr');
      document.body.classList.remove('rtl');
    }
  }, [language, direction]);

  const changeLanguage = async (lang: LanguageCode) => {
    try {
      // Change i18n language (this will automatically save to localStorage as 'i18nextLng')
      await i18n.changeLanguage(lang);

      // Ensure language is persisted to localStorage (backup in case i18next doesn't do it)
      localStorage.setItem('i18nextLng', lang);

      // Update local state
      setLanguage(lang);
      setDirection(LANGUAGES[lang].dir);

      // Save to user profile if logged in
      if (user) {
        try {
          // Update user preferences in the backend
          await authApi.updateProfile({
            preferences: {
              ...user.preferences,
              language: lang
            }
          });

          // Update local user state
          const updatedUser = {
            ...user,
            preferences: {
              ...user.preferences,
              language: lang
            }
          };
          updateUser(updatedUser);
        } catch (apiError) {
          console.error('Failed to save language preference to backend:', apiError);
          // Don't throw - the language still changed locally
        }
      }
    } catch (error) {
      console.error('Failed to change language:', error);
      throw error;
    }
  };

  const isRTL = direction === 'rtl';

  return (
    <I18nContext.Provider value={{ language, direction, changeLanguage, isRTL }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = (): I18nContextType => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
};
